import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {validateProcedure} from 'procedures/design/validation/validateProcedure';
import {
  ProcedurePageFlowchartVersionQuery,
  ProcedurePageFlowchartVersionQueryVariables,
} from 'procedures/graphql/__generated/ProcedurePageFlowchartVersionQuery';
import {
  ProcedurePageThinQuery,
  ProcedurePageThinQueryVariables,
} from 'procedures/graphql/__generated/ProcedurePageThinQuery';
import {
  PROCEDURE_PAGE_FLOWCHART_VERSION_QUERY,
  PROCEDURE_PAGE_THIN_QUERY,
} from 'procedures/graphql/ProceduresQueries';
import parseProcedureFromNetwork from 'procedures/network/parseProcedureFromNetwork';
import {ProcedureWithMetadata} from 'procedures/ProcedureDomainTypes';
import {NetworkProcedure} from 'procedures/ProcedurePageComponents';
import {generateIdToCidMap} from 'procedures/procedurePagePersistenceUtils';
import {
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import useBioentitiesForProcedures, {
  getBioentityIdsFromFlowchart,
} from 'procedures/utils/useBioentitiesForProcedures';

export enum ProcedureValidationErrorType {
  NOT_PUBLISHED = 'NOT_PUBLISHED',
  NOT_ACCESSIBLE = 'NOT_ACCESSIBLE',
  NOT_LOADED = 'NOT_LOADED',
  INTERNAL_VALIDATION_ERRORS = 'INTERNAL_VALIDATION_ERRORS',
}
// An "external" validation error precedes inspection of the actual flowchart contents
type ExternalValidationErrorType = Exclude<ProcedureValidationErrorType, 'INTERNAL_VALIDATION_ERRORS'>;

export type ValidateProcedureResult =
  // If no procedure ID is provided, we return null
  | null
  | (
      | {
          // A valid procedure
          isValid: true;
        }
      | {
          // A procedure with a single EXTERNAL error (e.g. not loaded/published/accessible, etc.)
          isValid: false;
          errorType: ExternalValidationErrorType;
          errors?: never;
        }
      | {
          // A procedure with INTERNAL validation errors (e.g. invalid steps, missing parameters, etc.)
          isValid: false;
          errorType: ProcedureValidationErrorType.INTERNAL_VALIDATION_ERRORS;
          errors: Array<ProcedureValidationError>;
        }
    );

/**
 * Given a procedure ID, we load the procedure and validate it.
 *
 * Returns:
 *  - null if the procedure ID is null
 *  - {isValid}, with either a `generalError` string or a full list of validation errors.
 *
 *  See the `ValidateProcedureResult` type above for details.
 */
export default function useValidateProcedureById(procedureId?: string | null): ValidateProcedureResult {
  // We need to load some global schemas for validation
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const tagSchemas = useAllTagSchemasWithStandardShape();

  // Load the procedure, and check for external errors
  const procedureOrError = useFullProcedureLoader(procedureId ?? null);

  // Final prep for internal validation: load the referenced bioentities
  const bioentityIdsInFlowchart =
    procedureOrError.procedureWithMetadata != null
      ? getBioentityIdsFromFlowchart(procedureOrError.procedureWithMetadata.flowchart)
      : [];
  const bioentitiesForProcedures = useBioentitiesForProcedures(bioentityIdsInFlowchart);

  if (procedureId == null) {
    return null;
  }

  // Report external errors that were found when loading the procedure
  if (procedureOrError.error != null) {
    return {
      isValid: false,
      errorType: procedureOrError.error,
    };
  }

  // Finally: inspect the contents of the procedure for internal validation errors
  const validationErrors = validateProcedure(
    procedureOrError.procedureWithMetadata.flowchart,
    procedureOrError.procedureWithMetadata.metadata,
    {
      tagSchemasForProcedures,
      tagSchemas,
      bioentitiesForProcedures,
    }
  );

  return validationErrors.length === 0
    ? {isValid: true}
    : {
        isValid: false,
        errorType: ProcedureValidationErrorType.INTERNAL_VALIDATION_ERRORS,
        errors: validationErrors,
      };
}

/**
 * Inspects the result of a ProcedurePageThinQuery to retrieve the ID of its published flowchart version.
 * It may also return an error state.
 *
 * NOTE: If procedure publishing is OFF, we inspect the LATEST flowchart version instead.
 */
function getPublishedFlowchartVersionId(
  procedureQueryResult: SkippableBenchlingQueryResult<ProcedurePageThinQuery, ProcedurePageThinQueryVariables>
): {error: ExternalValidationErrorType} | string {
  // Loading state
  if (procedureQueryResult.type !== QueryResultType.SUCCESS) {
    return {error: ProcedureValidationErrorType.NOT_LOADED};
  }

  // Auth issues
  if (isInaccessible(procedureQueryResult.data.procedure)) {
    return {error: ProcedureValidationErrorType.NOT_ACCESSIBLE};
  }

  const flowchart = procedureQueryResult.data.procedure.procedureFlowchart;

  let versionIdToCheck: string | undefined;
  if (BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING) {
    versionIdToCheck = flowchart.publishedFlowchartVersion?.id;
  } else {
    assert(
      !isInaccessible(flowchart.latestFlowchartVersion),
      'Expected latest flowchart version to be always accessible for accessible procedure when publishing is disabled'
    );
    versionIdToCheck = flowchart.latestFlowchartVersion.id;
  }

  if (versionIdToCheck == null) {
    // The "latest" flowchart is never null; this check is only meaningful when publishing is enabled
    return {error: ProcedureValidationErrorType.NOT_PUBLISHED};
  }
  return versionIdToCheck;
}

/**
 * @package Exported for testing/mocking
 */
export function useThinProcedureQuery(
  procedureId: string | null
): SkippableBenchlingQueryResult<ProcedurePageThinQuery, ProcedurePageThinQueryVariables> {
  return useSkippableBenchlingQuery<ProcedurePageThinQuery, ProcedurePageThinQueryVariables>(
    PROCEDURE_PAGE_THIN_QUERY,
    {
      variables: {procedureId: procedureId || ''},
      skip: procedureId == null,
    }
  );
}

/**
 * @package Exported for testing/mocking
 */
export function useFlowchartVersionQuery(
  procedureId: string | null,
  flowchartVersionId: string | null
): SkippableBenchlingQueryResult<
  ProcedurePageFlowchartVersionQuery,
  ProcedurePageFlowchartVersionQueryVariables
> {
  return useSkippableBenchlingQuery<
    ProcedurePageFlowchartVersionQuery,
    ProcedurePageFlowchartVersionQueryVariables
  >(PROCEDURE_PAGE_FLOWCHART_VERSION_QUERY, {
    variables: {procedureFlowchartVersionId: flowchartVersionId || ''},
    skip: procedureId == null || flowchartVersionId == null,
  });
}

type UseFullProcedureLoaderResult =
  | {procedureWithMetadata: ProcedureWithMetadata; error?: never}
  | {procedureWithMetadata?: never; error: ExternalValidationErrorType};

/**
 * Hook to load a full procedure object, including its flowchart version. This is a shorthand
 * version of all the loading & data-juggling we do in ProcedurePage. It will report "external"
 * errors if any are found; otherwise, it returns the full procedure with metadata.
 *
 * TODO(BNCH-123722): Update this function accordingly to be used by validation for PoW procedures. In PoW procedures,
 * we will use this function to load the procedure without having the flowchart config version loaded, because
 * in PoW procedures does not need to handle loading of the flowchart config version.
 */
function useFullProcedureLoader(procedureId: string | null): UseFullProcedureLoaderResult {
  // Load the "thin" procedure (i.e., everything except the flowchart version, which holds the nodes & edges)
  const thinProcedureQueryResult = useThinProcedureQuery(procedureId);

  const flowchartVersionIdOrExternalError = getPublishedFlowchartVersionId(thinProcedureQueryResult);
  const flowchartVersionId: string | null =
    typeof flowchartVersionIdOrExternalError === 'string' ? flowchartVersionIdOrExternalError : null;

  // Load the flowchart version
  const flowchartVersionQueryResult = useFlowchartVersionQuery(procedureId, flowchartVersionId);

  if (procedureId == null || thinProcedureQueryResult.type !== QueryResultType.SUCCESS) {
    return {error: ProcedureValidationErrorType.NOT_LOADED};
  }
  if (isInaccessible(thinProcedureQueryResult.data.procedure)) {
    return {error: ProcedureValidationErrorType.NOT_ACCESSIBLE};
  }
  if (typeof flowchartVersionIdOrExternalError === 'object') {
    return {error: flowchartVersionIdOrExternalError.error};
  }
  if (flowchartVersionQueryResult.type !== QueryResultType.SUCCESS) {
    return {error: ProcedureValidationErrorType.NOT_LOADED};
  }

  const procedure = thinProcedureQueryResult.data.procedure;
  const flowchartVersion = flowchartVersionQueryResult.data.procedureFlowchartVersion;
  if (isInaccessible(flowchartVersion)) {
    return {error: ProcedureValidationErrorType.NOT_ACCESSIBLE};
  }

  const networkProcedure: NetworkProcedure = {
    ...procedure,
    procedureFlowchart: {
      ...procedure.procedureFlowchart,
      // We're stuffing the published version into a field called "latest". This would be dangerous,
      // but it's only used to validate, then tossed.
      latestFlowchartVersion: flowchartVersion,
    },
  };
  const idToCidMap = generateIdToCidMap(networkProcedure);
  const getCid: (id: string, errorContext: object) => string = (id, errorContext) => {
    const cid = idToCidMap[id];
    if (cid != null) {
      return cid;
    } else {
      throw new Error(`No CID found for ID ${id} in the procedure. Context: ${JSON.stringify(errorContext)}`);
    }
  };
  return {procedureWithMetadata: parseProcedureFromNetwork(networkProcedure, getCid)};
}
